import type { BillingInterval } from "store/slices/reduxApi/billing";

import { ReactComponent as UsersIcon } from "../../assets/icons/common/users.svg";
import { ManagePlanQuantityStepper } from "./ManagePlanQuantityStepper";
import { ManagePlanUsageBar } from "./ManagePlanUsageBar";

type ManagePlanSeatPanelProps = {
  billingInterval: BillingInterval;
  currentBuilderSeats: number;
  currentBuilderSeatUsage: number;
  builderSeatCountUnavailable?: boolean;
  builderSeats: number;
  onBuilderSeatsChange: (value: number) => void;
  pricePerSeatAnnual: number;
  pricePerSeatMonthly: number;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ManagePlanSeatPanel({
  billingInterval,
  currentBuilderSeats,
  currentBuilderSeatUsage,
  builderSeatCountUnavailable = false,
  builderSeats,
  onBuilderSeatsChange,
  pricePerSeatAnnual,
  pricePerSeatMonthly,
}: ManagePlanSeatPanelProps) {
  const builderSeatPrice =
    billingInterval === "annual" ? pricePerSeatAnnual : pricePerSeatMonthly;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";

  return (
    <div
      data-test="manage-seat-panel"
      className="flex flex-col overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_rgba(31,38,51,0.12)]"
    >
      <div className="flex items-center gap-2 border-b border-[#e8eaed] bg-[#f9fafb] px-4 py-3">
        <UsersIcon className="h-4 w-4" />
        <span className="text-xs font-medium leading-4 text-[#1f2633]">
          AI Builder Licenses
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs leading-4 text-[#6c7689]">Current plan</span>
          <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
            {currentBuilderSeats} AI Builder Licenses
          </span>
        </div>

        <ManagePlanUsageBar
          used={currentBuilderSeatUsage}
          total={builderSeats}
        />

        <div className="h-0 border-b border-[#e8eaed]" />

        <div className="flex flex-col gap-3">
          <span className="text-xs leading-4 text-[#6c7689]">
            Adjust AI Builder Licenses ({formatUsd(builderSeatPrice)}
            /license{intervalSuffix})
          </span>
          <ManagePlanQuantityStepper
            value={builderSeats}
            min={Math.max(1, currentBuilderSeatUsage)}
            disabled={builderSeatCountUnavailable}
            decrementTooltip={
              currentBuilderSeatUsage > 0
                ? `Cannot reduce below ${currentBuilderSeatUsage} assigned seat${currentBuilderSeatUsage === 1 ? "" : "s"}. Unassign seats first.`
                : undefined
            }
            onChange={onBuilderSeatsChange}
          />
        </div>
      </div>
    </div>
  );
}
